import type { ReactNode } from "react";
import { createContext, createElement, useContext, useMemo } from "react";
import { useAuth as useClerkAuth, useUser, useClerk } from "@clerk/clerk-react";
import type { AppUser } from "../types/auth";

interface AuthContextValue {
  user: AppUser | null;
  loading: boolean;
  configured: boolean;
  authMode: "clerk" | "demo";
  isSignedIn: boolean;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const { isLoaded, isSignedIn: clerkIsSignedIn } = useClerkAuth();
  const { user: clerkUser, isLoaded: isUserLoaded } = useUser();
  const { signOut: clerkSignOut } = useClerk();

  const user = useMemo<AppUser | null>(() => {
    // 只允许邮箱/Google登录的用户，排除匿名用户
    if (!clerkIsSignedIn || !clerkUser || !clerkUser.primaryEmailAddress) return null;
    return {
      id: clerkUser.id,
      email: clerkUser.primaryEmailAddress?.emailAddress || "",
      nickname: clerkUser.fullName || clerkUser.username || clerkUser.primaryEmailAddress?.emailAddress?.split("@")[0] || "User",
      avatarUrl: clerkUser.imageUrl,
      identities: clerkUser.externalAccounts.map((acc) => acc.provider) as string[],
    };
  }, [clerkIsSignedIn, clerkUser]);

  // 等待 Clerk 完全加载
  const loading = !isLoaded || (clerkIsSignedIn && !isUserLoaded);

  const value = useMemo<AuthContextValue>(
    () => ({
      user,
      loading,
      configured: true,
      authMode: "clerk",
      isSignedIn: !!clerkIsSignedIn && !!user,
      async signOut() {
        await clerkSignOut();
      },
    }),
    [loading, clerkIsSignedIn, user, clerkSignOut],
  );

  return createElement(AuthContext.Provider, { value }, children);
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
